import { logger } from './logger';

export interface CarfaxRequest {
  vin: string;
  carfax_Requested: string; // ISO timestamp
  carfax_Form_Submitted?: string; // ISO timestamp
  carfax_Sent?: string; // ISO timestamp
}

const CARFAX_REQUESTS_KEY = 'chatbot_carfax_requests';

export const getCarfaxRequests = (): CarfaxRequest[] => {
  try {
    const stored = localStorage.getItem(CARFAX_REQUESTS_KEY);
    if (!stored) return [];
    return JSON.parse(stored);
  } catch (error) {
    logger.error('Error getting carfax requests:', error);
    return [];
  }
};

export const addCarfaxRequest = (vin: string): void => {
  try {
    logger.log('=== ADDING CARFAX REQUEST ===');
    logger.log('VIN:', vin);
    
    const requests = getCarfaxRequests();
    const timestamp = new Date().toISOString();
    
    // Check if request already exists for this VIN
    const existingIndex = requests.findIndex(req => req.vin === vin);
    
    if (existingIndex >= 0) {
      // Update existing request with new timestamp
      requests[existingIndex].carfax_Requested = timestamp;
      logger.log('Updated existing carfax request for VIN:', vin);
    } else {
      // Add new request
      requests.push({
        vin,
        carfax_Requested: timestamp
      });
      logger.log('Added new carfax request for VIN:', vin);
    }
    
    localStorage.setItem(CARFAX_REQUESTS_KEY, JSON.stringify(requests));
    logger.log('Carfax requests updated:', requests);
  } catch (error) {
    logger.error('Error adding carfax request:', error);
  }
};

export const updateCarfaxFormSubmitted = (vin: string): void => {
  try {
    logger.log('=== UPDATING CARFAX FORM SUBMITTED ===');
    logger.log('VIN:', vin);
    
    const requests = getCarfaxRequests();
    const timestamp = new Date().toISOString();
    
    const existingIndex = requests.findIndex(req => req.vin === vin);
    
    if (existingIndex >= 0) {
      requests[existingIndex].carfax_Form_Submitted = timestamp;
      localStorage.setItem(CARFAX_REQUESTS_KEY, JSON.stringify(requests));
      logger.log('Updated carfax form submitted for VIN:', vin, 'at', timestamp);
    } else {
      logger.warn('No existing carfax request found for VIN:', vin);
    }
  } catch (error) {
    logger.error('Error updating carfax form submitted:', error);
  }
};

export const updateCarfaxSent = (vin: string): void => {
  try {
    logger.log('=== UPDATING CARFAX SENT ===');
    logger.log('VIN:', vin);
    
    const requests = getCarfaxRequests();
    const timestamp = new Date().toISOString();
    
    const existingIndex = requests.findIndex(req => req.vin === vin);
    
    if (existingIndex >= 0) {
      requests[existingIndex].carfax_Sent = timestamp;
      localStorage.setItem(CARFAX_REQUESTS_KEY, JSON.stringify(requests));
      logger.log('Updated carfax sent for VIN:', vin, 'at', timestamp);
    } else {
      logger.warn('No existing carfax request found for VIN:', vin);
    }
  } catch (error) {
    logger.error('Error updating carfax sent:', error);
  }
};

export const getCarfaxRequestByVin = (vin: string): CarfaxRequest | undefined => {
  const requests = getCarfaxRequests();
  return requests.find(req => req.vin === vin);
};

export const shouldShowCarfaxForm = (vin: string): boolean => {
  const request = getCarfaxRequestByVin(vin);
  
  if (!request) {
    logger.log('No carfax request found for VIN:', vin, '- should show form');
    return true;
  }
  
  // If no form submitted timestamp, show form
  if (!request.carfax_Form_Submitted) {
    logger.log('No form submitted timestamp for VIN:', vin, '- should show form');
    return true;
  }
  
  // Check if form was submitted within last 48 hours
  const submittedDate = new Date(request.carfax_Form_Submitted);
  const now = new Date();
  const hoursDiff = (now.getTime() - submittedDate.getTime()) / (1000 * 60 * 60);
  
  const shouldShow = hoursDiff > 48;
  logger.log('Carfax form submitted', hoursDiff.toFixed(1), 'hours ago for VIN:', vin, '- should show form:', shouldShow);
  
  return shouldShow;
};

export const shouldShowCarfaxReport = (vin: string): boolean => {
  const request = getCarfaxRequestByVin(vin);
  
  if (!request || !request.carfax_Form_Submitted) {
    logger.log('No carfax form submitted for VIN:', vin, '- should not show report');
    return false;
  }
  
  // Check if form was submitted within last 30 days
  const submittedDate = new Date(request.carfax_Form_Submitted);
  const now = new Date();
  const daysDiff = (now.getTime() - submittedDate.getTime()) / (1000 * 60 * 60 * 24);
  
  const shouldShow = daysDiff <= 30;
  logger.log('Carfax form submitted', daysDiff.toFixed(1), 'days ago for VIN:', vin, '- should show report:', shouldShow);
  
  return shouldShow;
};